// Messages-domain slice: the primitives that own every mutation to
// sessionDetail.messages (_replaceMessages / _upsertMessage / _removeMessage /
// _applyStreamChunk). These are shared infrastructure — the session, stream,
// and conversation slices all mutate messages through these instead of each
// reimplementing the splicing + optimistic→server reconciliation.
//
// Extracting them removed the back-edge where the sessions and stream slices
// reached into the CONVERSATION slice just to touch messages. The remaining
// cross-slice edges here are intentional, not an accident: messages calls back
// into the sessions slice (_syncSessionListFromDetail) and writes a little
// stream-slice state (_workerStatuses / _teamActivityByRun) on session switch.
// So messages and sessions are co-dependent infrastructure — a deliberate
// mutual edge, not a strict one-way graph.
//
// Composed into useAgentStore via slice composition (single create()).
// Cross-slice calls go through get(), which resolves to the full store.

import { getLiveStreamController } from "./agentStreamController";
import {
  critical as _crit,
  findUnresolvedStreamParts,
  summarizeUnresolvedStreamParts,
} from "../../utils/ChatStreamLogger";
import {
  sortSessionMessages,
} from "./conversation/chatMessageOrder";
import {
  _withAppMessageId,
} from "./agentStoreUtils";

export function createMessagesSlice(set, get) {
  return {
  // ── Message primitives ─────────────────────────────────────
  // Every mutation to sessionDetail.messages goes through one of
  // these three functions. Nothing else touches the array.
  // _upsertMessage handles optimistic→server reconciliation via clientRequestId.

  _replaceMessages: (chatSessionId, serverMessages, serverDetail) => {
    const currentId = get().sessionDetail?.chatSessionId;
    const targetSessionId = serverDetail?.chatSessionId ?? chatSessionId ?? null;
    const isSwitchingSession = targetSessionId !== (currentId ?? null);
    if (isSwitchingSession) {
      set({ _workerStatuses: {}, _teamActivityByRun: {} });
    }
    set((s) => {
      // Normalize server messages so `.id` equals the app message id; see
      // `_withAppMessageId` for why this invariant matters.
      const incoming = (serverMessages || []).map(_withAppMessageId);

      // Only preserve local-only messages (optimistic, streaming assistant)
      // when staying on the SAME session. When switching sessions, the old
      // session's messages must not leak into the new one.
      if (!isSwitchingSession) {
        const currentMsgs = s.sessionDetail?.messages || [];
        const incomingIds = new Set();
        for (const m of incoming) {
          if (m.messageId) incomingIds.add(m.messageId);
          if (m.id) incomingIds.add(m.id);
        }

        const localOnly = currentMsgs.filter((m) => {
          if (m._optimistic) return true;
          const mid = m.messageId || m.id;
          if (mid && !incomingIds.has(mid) && m.role === "assistant") return true;
          return false;
        });

        if (localOnly.length > 0) {
          // A live assistant turn not yet in the snapshot is preserved by the
          // id test in `localOnly` above; once the snapshot includes that id it
          // is excluded. The live and persisted copies share one id (the
          // server's iterationMessageId), so no content/parts heuristic is
          // needed to collapse them. Optimistic messages still reconcile once
          // the server echoes them — by clientRequestId, else by role+content
          // for sends without one.
          const nonDup = localOnly.filter((m) => {
            if (m._optimistic && m.clientRequestId && incoming.some((im) => im.clientRequestId === m.clientRequestId)) {
              return false;
            }
            if (m._optimistic && incoming.some((im) => im.role === m.role && im.content === m.content)) {
              return false;
            }
            return true;
          });
          if (nonDup.length > 0) incoming.push(...nonDup);
        }
      }

      const orderedMessages = sortSessionMessages(incoming);

      const detail = serverDetail
        ? { ...serverDetail, messages: orderedMessages }
        : s.sessionDetail
          ? { ...s.sessionDetail, chatSessionId, messages: orderedMessages }
          : { chatSessionId, messages: orderedMessages, status: "IN_PROGRESS" };

      return { sessionDetail: detail };
    });
    const replacedMessages =
      get().sessionDetail?.chatSessionId === targetSessionId
        ? get().sessionDetail?.messages
        : null;
    const unresolvedIncoming = findUnresolvedStreamParts(replacedMessages);
    if (unresolvedIncoming.length > 0) {
      _crit(
        "AgentStore",
        "warn",
        `_replaceMessages unresolved stream parts session=${targetSessionId || chatSessionId || "?"} count=${unresolvedIncoming.length} [${summarizeUnresolvedStreamParts(replacedMessages)}]`,
        { unresolvedParts: unresolvedIncoming }
      );
    }
    if (targetSessionId) {
      get()._syncSessionListFromDetail(targetSessionId);
    }
  },

  _upsertMessage: (chatSessionId, msg) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) {
        return {};
      }
      if (!msg) return {};

      const id = msg.messageId || msg.id;
      const before = detail.messages || [];

      // Reconciling a live copy with its persisted server copy: replace in place
      // then re-order, because the server copy carries the messageSequence the
      // live copy lacked. Without the re-sort a marker that arrived first would
      // stay stuck ahead of the turn it evaluated.
      if (id) {
        const idx = before.findIndex(
          (m) => (m.messageId && m.messageId === id) || (m.id && m.id === id)
        );
        if (idx !== -1) {
          const msgs = [...before];
          msgs[idx] = msg;
          const sorted = sortSessionMessages(msgs);
          return { sessionDetail: { ...detail, messages: sorted } };
        }
      }

      if (msg.clientRequestId || (msg.role === "user" && !msg._optimistic)) {
        const optIdx = msg.clientRequestId
          ? before.findIndex((m) => m._optimistic && m.clientRequestId === msg.clientRequestId)
          : before.findIndex((m) => m._optimistic && m.role === "user" && m.content === msg.content);
        if (optIdx !== -1) {
          const msgs = [...before];
          const prior = before[optIdx];
          msgs[optIdx] = {
            ...msg,
            senderDisplayName:
              msg.senderDisplayName || prior.senderDisplayName || undefined,
            senderName: msg.senderName || prior.senderName || undefined,
          };
          return { sessionDetail: { ...detail, messages: sortSessionMessages(msgs) } };
        }
      }

      // The live streamed copy and the server-persisted copy of an assistant
      // turn share one id (the server's iterationMessageId), so the id-match
      // path above reconciles them. No content/parts dedup is needed.
      const appended = sortSessionMessages([...before, msg]);
      return {
        sessionDetail: {
          ...detail,
          messages: appended,
        },
      };
    });
    get()._syncSessionListFromDetail(chatSessionId);
  },

  _removeMessage: (chatSessionId, messageId) => {
    set((s) => {
      const detail = s.sessionDetail;
      if (!detail || detail.chatSessionId !== chatSessionId) return {};
      const msgs = (detail.messages || []).filter((m) => m.messageId !== messageId);
      return { sessionDetail: { ...detail, messages: msgs } };
    });
  },

  // Applies one live UI-chunk SSE event. Thin delegate to the stream controller
  // (see conversation/stream/streamController.js), which owns the reducer
  // cursors, the intra-frame flush buffer, and the animation-frame throttle and
  // writes the in-flight assistant turn straight into sessionDetail.messages —
  // the single source of truth. Kept as a store method so the SSE wiring and
  // tests can drive it via the store.
  _applyStreamChunk: (eventName, data, sessionId, seq = null) => {
    getLiveStreamController().applyStreamChunk(eventName, data, sessionId, seq);
  },
  };
}
